import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Pathname } from 'Routes';
import { AppContext, AuthContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { isLifetimePlan } from 'Utils/paymentGate';
import logo from 'Assets/Login Logo.png';
import Right from 'Assets/small-right.png';
import Left from 'Assets/small-left.png';
import Plus from 'Assets/plus-button.svg';
import avatar01 from 'Assets/01.png';
import avatar02 from 'Assets/02.png';
import avatar03 from 'Assets/03.png';
import avatar04 from 'Assets/04.png';
import avatar05 from 'Assets/05.png';
import avatar06 from 'Assets/06.png';
import avatar07 from 'Assets/07.png';
import avatar08 from 'Assets/08.png';
import avatar09 from 'Assets/09.png';
import avatar10 from 'Assets/10.png';
import avatar11 from 'Assets/11.png';
import avatar12 from 'Assets/12.png';
import { Button } from 'Components';
import 'react-phone-number-input/style.css';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { createRecaptchaVerifier, sendFirebaseOtp, mapFirebaseAuthError } from 'Api/firebaseAuthClient';
import { encryptedKeyForCredentials } from 'Commons';
import { NAME_MAX_LENGTH, NAME_LIMIT_MESSAGE, nameLimitHandlers } from 'Utils/nameLimit';
import { Input } from '../components/input-field';
import styles from './SignInClassic.module.css';

// Predefined avatar collection
const AVATAR_COLLECTION = [
  avatar11,
  avatar01,
  avatar02,
  avatar03,
  avatar04,
  avatar05,
  avatar06,
  avatar07,
  avatar08,
  avatar09,
  avatar10,
  avatar12,
];

/**
 * Classic (pre-redesign) avatar step — new-user signup and gift lanes.
 * From feature/affiliate-membership (latest old UI), so it carries the
 * "phone number field showing in gift flow" fix and the OTP text changes.
 */
export const AvatarClassic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  console.log('location', location);
  // TEMP (dev/QA): /avatar?onboarding=1 walks the real post-signup lane without a
  // full signup — treated the same as arriving with location.state.fromVerificationCode.
  // Remove this override (and restore the plain location.state checks) when done.
  const cameFromVerification = !!location.state?.fromVerificationCode
    || new URLSearchParams(location.search).get('onboarding') === '1';
  const {
    setAppSnackbar, setUserDetails, userDetails, autoSignIn,
    subscriptionDetails, allPlans, getSubscription,
  } = useContext(AppContext);

  const showNameLimitError = () => {
    setAppSnackbar({ isVisible: true, type: 'error', message: NAME_LIMIT_MESSAGE });
  };

  // The quick-user lane ends on /quick-user-payment, which exists purely to
  // capture a card. A Lifetime account has already paid once and forever, so
  // there is nothing to capture — send them straight to their Projector
  // instead. Pull the subscription in first so the check isn't made against a
  // still-empty context.
  useEffect(() => {
    getSubscription?.();
  }, []);

  const skipPaymentStep = isLifetimePlan(subscriptionDetails, allPlans);
  const quickUserNextPath = skipPaymentStep
    ? Pathname.profile.basePath
    : Pathname.authentication.quickUserPayment;

  // Centralized post-avatar routing. Four call sites (success of name
  // update, success of avatar update, the direct Continue, the Skip) all
  // resolve to the same destination based on which onboarding lane we're
  // in. New-user lanes (fromSetPassword / fromVerificationCode) detour
  // through the interview-record intro page; gift flow and existing-user
  // edits go straight to their target as before.
  const goToNextAfterAvatar = () => {
    if (location.state?.isGiftFlow) {
      navigate(Pathname.giftFlow.content);
      return;
    }
    if (location.state?.fromSetPassword) {
      navigate(Pathname.interview.intro, {
        state: { nextPath: quickUserNextPath },
      });
      return;
    }
    if (cameFromVerification) {
      navigate(Pathname.interview.intro, {
        state: { nextPath: Pathname.authentication.toTrail },
      });
      return;
    }
    navigate(Pathname.profile.basePath);
  };
  const { getToken } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [selectedAvatar, setSelectedAvatar] = useState(userDetails?.dpURL ? null : avatar11);
  const [avatar, setAvatar] = useState(userDetails?.dpURL);
  const [previewImage, setPreviewImage] = useState(userDetails?.dpURL || avatar11);
  const [hasAvatarChanged, setHasAvatarChanged] = useState(false);
  const [firstName, setFirstName] = useState(userDetails?.firstname || '');
  const [lastName, setLastName] = useState(userDetails?.lastname || '');
  const [currentIndex, setCurrentIndex] = useState(0);

  // ── Quick-user mobile verification ──
  // Accounts arriving from the set-password lane still have an unverified
  // number, so Continue detours through an OTP step here before the normal
  // flow resumes. The SMS is sent by Firebase Phone Auth straight from the
  // browser (same as the sign-up screen); the backend is told the outcome via
  // verifyOtpFirebase.
  // set-password sends BOTH lanes here with fromSetPassword — the gift
  // recipient just carries isGiftFlow (and a ?gift=true query) alongside it.
  // Only the quick-user lane gets the phone field / OTP step.
  const isGiftFlow = !!location.state?.isGiftFlow
    || new URLSearchParams(location.search).get('gift') === 'true';
  const isQuickUser = !!location.state?.fromSetPassword && !isGiftFlow;
  const mobileAlreadyVerified = parseInt(userDetails?.verify_mobile, 10) === 1;
  const needsMobileVerification = isQuickUser && !mobileAlreadyVerified;
  const accountEmail = userDetails?.email
    || sessionStorage.getItem(encryptedKeyForCredentials.email);

  const [mobile, setMobile] = useState(userDetails?.mobile || '');
  const [sendingOtp, setSendingOtp] = useState(false);
  const recaptchaVerifierRef = useRef(null);

  const getRecaptchaVerifier = () => {
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = createRecaptchaVerifier('firebase-recaptcha-container-avatar');
    }
    return recaptchaVerifierRef.current;
  };

  // Saves the number against the account, has Firebase text the code, then
  // hands over to the existing code-entry screen. `nextPath` tells that screen
  // to come back to this lane's destination once the code checks out, instead
  // of running its own post-signin routing.
  const sendMobileOtp = async () => {
    if (!mobile || !isValidPhoneNumber(mobile)) {
      setAppSnackbar({ isVisible: true, message: 'Please enter a valid mobile number' });
      return;
    }
    setSendingOtp(true);
    try {
      const { success, message, token } = await dataFetcher(endpoints.updateMyProfile, {
        mobile,
        email: accountEmail,
      });
      if (!success) {
        setAppSnackbar({
          isVisible: true,
          message: message || 'Oops! Something went wrong while updating phone number',
        });
        return;
      }
      sessionStorage.setItem('mvt', token);
      setUserDetails({ ...userDetails, mobile });
      await sendFirebaseOtp(mobile, getRecaptchaVerifier());
      // The code screen reads both of these out of sessionStorage.
      sessionStorage.setItem(encryptedKeyForCredentials.email, accountEmail);
      sessionStorage.setItem(encryptedKeyForCredentials.mobile, mobile);
      sessionStorage.setItem('#fs656gfhllh@#$', true);
      setAppSnackbar({ isVisible: true, message: 'Code sent to your mobile number' });
      navigate(Pathname.authentication.newverifyCode, {
        state: { nextPath: quickUserNextPath },
      });
    } catch (error) {
      // A used verifier can't send again — drop it so the retry mints a fresh one.
      recaptchaVerifierRef.current = null;
      setAppSnackbar({ isVisible: true, message: mapFirebaseAuthError(error) });
    } finally {
      setSendingOtp(false);
    }
  };

  // Where the quick-user lane goes once the profile is saved: the code screen
  // first, then on to whatever it did before.
  const goToQuickUserNext = () => {
    if (needsMobileVerification) {
      sendMobileOtp();
      return;
    }
    navigate(quickUserNextPath);
  };

  // Carousel configuration
  const AVATARS_PER_VIEW = 4;
  const maxIndex = Math.max(0, AVATAR_COLLECTION.length - AVATARS_PER_VIEW);

  // Carousel navigation functions
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => Math.max(0, prevIndex - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => Math.min(maxIndex, prevIndex + 1));
  };

  // SWR hook for name updates
  const { isValidating: updatingName, mutate: updateName } = useSWR(
    firstName || lastName
      ? [endpoints.updateMyProfile, firstName, lastName]
      : null,
    {
      fetcher: (url, inputFirstname, inputLastname) => dataFetcher(url, {
          firstname: inputFirstname,
          lastname: inputLastname,
        }),
      onSuccess: ({ success }) => {
        if (success) {
          setAppSnackbar({
            open: true,
            message: 'Name was updated successfully',
            severity: 'success',
          });
          // Refresh user details to show updated name
          autoSignIn();
          // Navigate based on flow: gift content for gift flow, welcome for new users, profile otherwise
          if (location.state?.isGiftFlow) {
            navigate(Pathname.giftFlow.content);
          } else if (location.state?.fromSetPassword) {
            goToQuickUserNext();
          } else if (cameFromVerification) {
            navigate(Pathname.authentication.chooseOccasion);
          } else {
            navigate(Pathname.profile.basePath);
          }
        } else {
          setAppSnackbar({
            open: true,
            message: 'Failed to update profile names',
            severity: 'error',
          });
        }
      },
      onError: () => {
        setAppSnackbar({
          open: true,
          message: 'Oops! Something went wrong while updating name',
          severity: 'error',
        });
      },
    }
  );
  // SWR hook for avatar updates
  const { isValidating: updatingAvatar, mutate: updateAvatar } = useSWR(
    hasAvatarChanged ? [endpoints.updateProfileImge, avatar] : null,
    {
      fetcher: (url, inputAvatar) => {
        // Send the image with data URL prefix for the API
        const imageData = inputAvatar.includes('data:image')
          ? inputAvatar
          : `data:image/jpeg;base64,${inputAvatar}`;
        return dataFetcher(url, { image: imageData });
      },
      onSuccess: ({ success, image }) => {
        if (success) {
          setAppSnackbar({
            open: true,
            message: 'Avatar was updated successfully',
            severity: 'success',
          });
          setUserDetails({ ...userDetails, dpURL: image });
          autoSignIn();
          // Update names if provided after avatar success
          if (firstName || lastName) {
            updateName();
          } else if (location.state?.isGiftFlow) {
            navigate(Pathname.giftFlow.content);
          } else if (location.state?.fromSetPassword) {
            goToQuickUserNext();
          } else if (cameFromVerification) {
            navigate(Pathname.authentication.chooseOccasion);
          } else {
            goToNextAfterAvatar();
          }
        } else {
          setAppSnackbar({
            open: true,
            message: 'Oops! Something went wrong while updating your avatar',
            severity: 'error',
          });
        }
      },
      onError: () => {
        setAppSnackbar({
          open: true,
          message: 'Oops! Something went wrong while updating your avatar',
          severity: 'error',
        });
      },
    }
  );

  // Convert image to base64
  const convertToBase64 = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setAppSnackbar({
          open: true,
          message: 'File size must be less than 5MB',
          severity: 'error',
        });
        return;
      }

      try {
        const base64 = await convertToBase64(file);
        setPreviewImage(base64);
        setSelectedAvatar(null);
        // Set avatar for SWR with full base64 data URL
        setAvatar(base64);
        setHasAvatarChanged(true);
      } catch (error) {
        setAppSnackbar({
          open: true,
          message: 'Error processing image',
          severity: 'error',
        });
      }
    }
  };
  // Auto-select avatar11 as default on component mount only if no existing dpURL
  useEffect(() => {
    const setDefaultAvatar = async () => {
      if (!userDetails?.dpURL) {
        try {
          const response = await fetch(avatar11);
          const blob = await response.blob();
          const base64 = await convertToBase64(blob);
          setAvatar(base64);
          setSelectedAvatar(avatar11); // Mark avatar11 as selected
          setHasAvatarChanged(true); // Need to update profile for users without dpURL
        } catch (error) {
          console.error('Error setting default avatar:', error);
        }
      }
    };
    setDefaultAvatar();
  }, []);
  // Handle avatar selection
  const handleAvatarSelect = async (avatarUrl) => {
    try {
      // Convert URL to base64
      const response = await fetch(avatarUrl);
      const blob = await response.blob();
      const base64 = await convertToBase64(blob);

      setSelectedAvatar(avatarUrl);
      setPreviewImage(avatarUrl);
      // Set avatar for SWR with full base64 data URL
      setAvatar(base64);
      setHasAvatarChanged(true);
    } catch (error) {
      setAppSnackbar({
        open: true,
        message: 'Error loading avatar',
        severity: 'error',
      });
    }
  };

  // Update names separately

  // Update profile using SWR
  const updateProfile = () => {
    if (!avatar) {
      setAppSnackbar({
        open: true,
        message: 'Please select an avatar or upload an image',
        severity: 'warning',
      });
      return;
    }

    // Only call avatar update API if user has changed the avatar
    if (hasAvatarChanged) {
      updateAvatar();
    } else if (firstName || lastName) {
      // If no avatar changes, just update names if provided
      updateName();
    } else if (location.state?.isGiftFlow) {
      navigate(Pathname.giftFlow.content);
    } else if (location.state?.fromSetPassword) {
      goToQuickUserNext();
    } else if (cameFromVerification) {
      navigate(Pathname.authentication.chooseOccasion);
    } else {
      goToNextAfterAvatar();
    }
  };

  return (
    <div className={styles.avatarContainer}>
      {/* Background accents */}
      <div className={styles.bgOverlay}>
        <div className={styles.bgAccent1} />
        <div className={styles.bgAccent2} />
        <div className={styles.bgCircle} />
      </div>

      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.header}>
          <Link to={Pathname.home}>
            <img src={logo} alt="Projector Logo" className={styles.logo} />
          </Link>
        </div>
        {/* Mobile-only preview above title */}
        {previewImage && (
          <div className={styles.mobilePreviewContainer}>
            <div className={styles.previewAvatar}>
              <img
                src={previewImage}
                alt="Selected avatar"
                className={styles.previewImage}
              />
            </div>
          </div>
        )}
        <div className={styles.titleWithPreview}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Choose Your Avatar</h1>
          <p className={styles.subtitle}>
            Select from our collection or upload your own
          </p>
        </div>
                    {/* Current Selection Preview - hidden on mobile */}
          {previewImage && (
            <div className={styles.previewContainer}>
              <div className={styles.previewAvatar}>
                <img
                  src={previewImage}
                  alt="Selected avatar"
                  className={styles.previewImage}
                />
              </div>
            </div>
          )}
        </div>

        {/* Avatar Selection Area */}
        <div className={styles.avatarSelectionArea}>
          {/* Name Fields - Hide if coming from verification code page */}
          {!cameFromVerification && (
            <div className={styles.nameFieldsContainer}>
              <div className={styles.nameFields}>
                <Input
                  automaticFocus
                  className={styles.customUserInput}
                  placeholder="First Name"
                  maxLength={NAME_MAX_LENGTH}
                  {...nameLimitHandlers(showNameLimitError)}
                  type="code"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value.slice(0, NAME_MAX_LENGTH))}
                  noLabel
                />
                <Input
                  className={styles.customUserInput}
                  placeholder="Last Name"
                  maxLength={NAME_MAX_LENGTH}
                  {...nameLimitHandlers(showNameLimitError)}
                  type="code"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value.slice(0, NAME_MAX_LENGTH))}
                  noLabel
                />
              </div>
              {needsMobileVerification && (
                <div className={styles.phoneNumberField}>
                  <PhoneInput
                    placeholder="Phone Number"
                    value={mobile}
                    onChange={setMobile}
                    countryCallingCodeEditable={false}
                    defaultCountry="US"
                  />
                </div>
              )}
            </div>
          )}

          {/* Avatar Carousel */}
          <div className={styles.collectionSection}>
            <div className={styles.carouselContainer}>
              <div
                role="button"
                tabIndex={0}
                className={styles.addButtonWrapper}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
              >
                <button
                  className={styles.addButton}
                  tabIndex={-1}
                >
                  <img
                    src={Plus}
                    alt="add img"
                    className={styles.avatarThumbnail}
                  />
                </button>
                <span className={styles.addButtonText}>Upload</span>
              </div>
              <button
                className={styles.carouselNavButton}
                onClick={goToPrevious}
                disabled={currentIndex === 0}
              >
                <img src={Left} alt="Previous" />
              </button>
              <div className={styles.avatarCarousel}>
                <div
                  className={styles.avatarTrack}
                  style={{
                    transform: `translateX(-${
                      currentIndex * (100 / AVATARS_PER_VIEW)
                    }%)`,
                  }}
                >
                  {AVATAR_COLLECTION.map((avatar, index) => (
                    <button
                      key={index}
                      onClick={() => handleAvatarSelect(avatar)}
                      className={`${styles.avatarButton} ${
                        selectedAvatar === avatar
                          ? styles.avatarButtonSelected
                          : ''
                      }`}
                    >
                      <img
                        src={avatar}
                        alt={`Avatar ${index + 1}`}
                        className={styles.avatarThumbnail}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <button
                className={styles.carouselNavButton}
                onClick={goToNext}
                disabled={currentIndex >= maxIndex}
              >
                <img src={Right} alt="Next" />
              </button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className={styles.hiddenInput}
          />

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <Button
              id="sasdasddd"
              blockText
              isBlue
              isDisabled={updatingAvatar || sendingOtp || !avatar || !previewImage
                || !firstName.trim() || !lastName.trim()
                || (needsMobileVerification && !mobile)}
              isFullWidth
              isLarge
              onClick={updateProfile}
            >
              {updatingAvatar ? ' Updating...' : 'Continue'}
            </Button>
          </div>
        </div>

        {/* Firebase Phone Auth mounts its invisible reCAPTCHA here */}
        <div id="firebase-recaptcha-container-avatar" />

        {/* Footer - hidden for the gift flow, and for the quick-user lane where
            the mobile number has to be entered and verified. */}
        {!location.state?.isGiftFlow && !needsMobileVerification && (
        <div className={styles.footer}>
          <button
            onClick={() => {
              // Save the default selected avatar before skipping
              if (hasAvatarChanged && avatar) {
                updateAvatar();
              } else if (location.state?.fromSetPassword) {
                goToQuickUserNext();
              } else if (cameFromVerification) {
                navigate(Pathname.authentication.chooseOccasion);
              } else {
                goToNextAfterAvatar();
              }
            }}
            className={styles.skipButton}
          >
            Skip for now
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default AvatarClassic;
